import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Calendar,
  Eye,
  Settings,
  AlertTriangle,
  MessageSquare,
  Edit2,
  ExternalLink,
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const CoachBilling = () => {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'billing';
  const [activeTab, setActiveTab] = useState(defaultTab);
  
  const [summaries, setSummaries] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [coachNotes, setCoachNotes] = useState([]);
  const [readNotes, setReadNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  // Sorting and filtering
  const [sortField, setSortField] = useState('coach_name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [coachFilter, setCoachFilter] = useState('all');
  const [coachStatusFilter, setCoachStatusFilter] = useState('active'); // New: status filter
  
  // Detail view
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedCoach, setSelectedCoach] = useState(null);
  
  // Rate dialog
  const [rateDialog, setRateDialog] = useState(false);
  const [rateCoach, setRateCoach] = useState(null);
  const [newRate, setNewRate] = useState('');

  // Edit Entry dialog
  const [editEntryDialog, setEditEntryDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editEntryData, setEditEntryData] = useState({
    hours_worked: '',
    notes: ''
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedMonth, coachStatusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const [summaryRes, coachesRes, unreadNotesRes, readNotesRes] = await Promise.all([
        api.get(`/admin/billing-summary?month=${parseInt(month)}&year=${parseInt(year)}&status=${coachStatusFilter}`),
        api.get(`/coaches?status=${coachStatusFilter}`),
        api.get('/coach-notes?is_read=false').catch(() => ({ data: [] })),
        api.get('/coach-notes?is_read=true').catch(() => ({ data: [] }))
      ]);
      setSummaries(summaryRes.data);
      setCoaches(coachesRes.data);
      setCoachNotes(unreadNotesRes.data);
      setReadNotes(readNotesRes.data);
    } catch (error) {
      console.error('Billing fetch error:', error);
      toast.error('Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkNoteRead = async (noteId) => {
    try {
      await api.put(`/coach-notes/${noteId}/read`);
      setCoachNotes(prev => prev.filter(n => n.id !== noteId));
      toast.success('Note marked as read');
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleReviewEntry = async (entryId, status) => {
    try {
      await api.put(`/time-entries/${entryId}/review`, { status });
      toast.success(`Entry ${status}`);
      fetchData();
      // Refresh selected coach data
      if (selectedCoach) {
        const updated = summaries.find(s => s.coach_id === selectedCoach.coach_id);
        setSelectedCoach(updated);
      }
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const handleSetRate = async () => {
    if (!newRate || parseFloat(newRate) <= 0) {
      toast.error('Please enter a valid hourly rate');
      return;
    }
    try {
      await api.put(`/admin/coaches/${rateCoach.id}/rate`, {
        hourly_rate: parseFloat(newRate)
      });
      toast.success('Hourly rate updated');
      setRateDialog(false);
      setNewRate('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update rate');
    }
  };

  const openRateDialog = (coach) => {
    setRateCoach(coach);
    setNewRate(coach.profile?.hourly_rate || '');
    setRateDialog(true);
  };

  const openDetailDialog = (summary) => {
    // Sort entries by date (most recent first)
    const sortedEntries = [...(summary.entries || [])].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
    setSelectedCoach({ ...summary, entries: sortedEntries });
    setDetailDialog(true);
  };

  const openEditEntryDialog = (entry) => {
    setEditingEntry(entry);
    setEditEntryData({
      hours_worked: entry.hours_worked.toString(),
      notes: ''
    });
    setEditEntryDialog(true);
  };

  const handleEditEntry = async () => {
    if (!editEntryData.notes.trim()) {
      toast.error('Notes are required when editing an entry');
      return;
    }
    
    const newHours = parseFloat(editEntryData.hours_worked);
    if (isNaN(newHours) || newHours <= 0) {
      toast.error('Please enter a valid number of hours');
      return;
    }

    setEditSubmitting(true);
    try {
      await api.put(`/admin/time-entries/${editingEntry.id}`, {
        hours_worked: newHours,
        admin_note: editEntryData.notes
      });
      toast.success('Entry updated. Please communicate this change to the coach.');
      setEditEntryDialog(false);
      setEditingEntry(null);
      fetchData();
      // Refresh the detail dialog
      if (selectedCoach) {
        const [year, month] = selectedMonth.split('-');
        const response = await api.get(`/admin/billing-summary?month=${parseInt(month)}&year=${parseInt(year)}`);
        const updated = response.data.find(s => s.coach_id === selectedCoach.coach_id);
        if (updated) {
          const sortedEntries = [...(updated.entries || [])].sort((a, b) => 
            new Date(b.date) - new Date(a.date)
          );
          setSelectedCoach({ ...updated, entries: sortedEntries });
        }
      }
    } catch (error) {
      console.error('Edit entry error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setEditSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-700">
            <XCircle className="h-3 w-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <AlertCircle className="h-3 w-3" />
            Pending
          </span>
        );
    }
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  const totalPendingHours = summaries.reduce((sum, s) => sum + s.pending_hours, 0);
  const totalApprovedAmount = summaries.reduce((sum, s) => sum + (s.total_amount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout title="Coach Billing">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Coach Billing">
      <div className="space-y-6" data-testid="coach-billing-page">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="billing" data-testid="tab-billing">
              <DollarSign className="h-4 w-4 mr-2" />
              Monthly Billing
            </TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-rates">
              <Settings className="h-4 w-4 mr-2" />
              Hourly Rates
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes" className="relative">
              <MessageSquare className="h-4 w-4 mr-2" />
              Coach Notes
              {coachNotes.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {coachNotes.length}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Billing Tab */}
          <TabsContent value="billing" className="mt-6 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Pending Review</p>
                      <p className="font-heading text-2xl font-bold text-amber-600">{totalPendingHours.toFixed(1)} hrs</p>
                    </div>
                    <AlertCircle className="h-8 w-8 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Payable</p>
                      <p className="font-heading text-2xl font-bold text-green-600">{cs()}{totalApprovedAmount.toFixed(2)}</p>
                    </div>
                    <DollarSign className="h-8 w-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Active Coaches</p>
                      <p className="font-heading text-2xl font-bold text-slate-900">{summaries.filter(s => s.total_hours > 0).length}</p>
                    </div>
                    <Users className="h-8 w-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Month Filter & Table */}
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-500" />
                    Coach Hours Summary
                  </CardTitle>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select value={coachStatusFilter} onValueChange={setCoachStatusFilter}>
                      <SelectTrigger className="w-32" data-testid="coach-status-filter">
                        <SelectValue placeholder="Active" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={coachFilter} onValueChange={setCoachFilter}>
                      <SelectTrigger className="w-48" data-testid="coach-filter">
                        <SelectValue placeholder="All Coaches" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Coaches</SelectItem>
                        {coaches.map((coach) => (
                          <SelectItem key={coach.id} value={coach.id}>
                            {coach.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="w-48" data-testid="billing-month-filter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {getMonthOptions().map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {summaries.length === 0 ? (
                  <div className="p-12 text-center">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No coaches found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'coach_name') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('coach_name');
                                setSortDirection('asc');
                              }
                            }}
                          >
                            Coach {sortField === 'coach_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="text-right cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'total_hours') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('total_hours');
                                setSortDirection('desc');
                              }
                            }}
                          >
                            Total Hours {sortField === 'total_hours' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="text-right cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'pending_hours') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('pending_hours');
                                setSortDirection('desc');
                              }
                            }}
                          >
                            Pending {sortField === 'pending_hours' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="text-right cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'approved_hours') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('approved_hours');
                                setSortDirection('desc');
                              }
                            }}
                          >
                            Approved {sortField === 'approved_hours' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead 
                            className="text-right cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'rejected_hours') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('rejected_hours');
                                setSortDirection('desc');
                              }
                            }}
                          >
                            Rejected {sortField === 'rejected_hours' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead className="text-right">Rate</TableHead>
                          <TableHead 
                            className="text-right cursor-pointer hover:bg-slate-50"
                            onClick={() => {
                              if (sortField === 'total_amount') {
                                setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortField('total_amount');
                                setSortDirection('desc');
                              }
                            }}
                          >
                            Amount Due {sortField === 'total_amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                          </TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summaries
                          .filter(s => coachFilter === 'all' || s.coach_id === coachFilter)
                          .sort((a, b) => {
                            const aVal = a[sortField] || '';
                            const bVal = b[sortField] || '';
                            if (sortDirection === 'asc') {
                              return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
                            } else {
                              return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
                            }
                          })
                          .map((summary) => (
                          <TableRow key={summary.coach_id} data-testid={`summary-${summary.coach_id}`}>
                            <TableCell className="font-medium">{summary.coach_name}</TableCell>
                            <TableCell className="text-right">{summary.total_hours.toFixed(1)}</TableCell>
                            <TableCell className="text-right">
                              {summary.pending_hours > 0 ? (
                                <span className="text-amber-600 font-medium">{summary.pending_hours.toFixed(1)}</span>
                              ) : (
                                <span className="text-slate-400">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right text-green-600 font-medium">
                              {summary.approved_hours.toFixed(1)}
                            </TableCell>
                            <TableCell className="text-right">
                              {(summary.rejected_hours || 0) > 0 ? (
                                <span className="text-rose-600 font-medium">{summary.rejected_hours.toFixed(1)}</span>
                              ) : (
                                <span className="text-slate-400">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {summary.hourly_rate ? `${cs()}${summary.hourly_rate.toFixed(2)}/hr` : (
                                <span className="text-slate-400">Not set</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {summary.total_amount ? `${cs()}${summary.total_amount.toFixed(2)}` : '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDetailDialog(summary)}
                                className="text-blue-600 hover:text-blue-700"
                                data-testid={`view-details-${summary.coach_id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rates Tab */}
          <TabsContent value="rates" className="mt-6">
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg">Coach Hourly Rates</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Set hourly rates for each coach. These rates are only visible to admins.</p>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coach Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Specialization</TableHead>
                        <TableHead className="text-right">Hourly Rate</TableHead>
                        <TableHead className="w-24"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {coaches.map((coach) => (
                        <TableRow key={coach.id} data-testid={`coach-rate-${coach.id}`}>
                          <TableCell className="font-medium">{coach.name}</TableCell>
                          <TableCell className="text-slate-600">{coach.email}</TableCell>
                          <TableCell className="text-slate-600">
                            {coach.profile?.specialization || '-'}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {coach.profile?.hourly_rate ? (
                              <span className="text-green-600">{cs()}{coach.profile.hourly_rate.toFixed(2)}/hr</span>
                            ) : (
                              <span className="text-slate-400">Not set</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openRateDialog(coach)}
                              data-testid={`set-rate-${coach.id}`}
                            >
                              Set Rate
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coach Notes Tab */}
          <TabsContent value="notes" className="mt-6">
            <Card className="border border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-blue-500" />
                  Unread Coach Notes
                </CardTitle>
                <p className="text-sm text-slate-500 mt-1">Notes from coaches about classes and trial players</p>
              </CardHeader>
              <CardContent className="p-0">
                {coachNotes.length === 0 ? (
                  <div className="p-12 text-center">
                    <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No unread notes</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {coachNotes.map((note) => (
                      <div key={note.id} className={`p-4 ${note.is_urgent ? 'bg-rose-50' : ''}`}>
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium">{note.coach_name}</span>
                              {note.is_urgent && (
                                <Badge variant="destructive" className="text-xs">Urgent</Badge>
                              )}
                              <Badge variant="outline" className="text-xs">
                                {note.note_type === 'class' ? 'Class Note' : 
                                 note.note_type === 'trial_player' ? 'Trial Player' : 'General'}
                              </Badge>
                            </div>
                            {note.class_name && (
                              <p className="text-sm text-slate-500 mb-1">Class: {note.class_name}</p>
                            )}
                            {note.player_name && (
                              <p className="text-sm text-slate-500 mb-1 flex items-center gap-2">
                                Player: {note.player_name}
                                {note.player_id && (
                                  <Link 
                                    to={`/admin/players?search=${encodeURIComponent(note.player_name)}`}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs"
                                    data-testid={`view-player-${note.id}`}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View Player
                                  </Link>
                                )}
                                {note.note_type === 'trial_player' && note.trial_id && (
                                  <Link 
                                    to={`/admin/trial-requests?search=${encodeURIComponent(note.player_name || note.trial_player_name || '')}`}
                                    className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 text-xs"
                                    data-testid={`view-trial-${note.id}`}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View Trial
                                  </Link>
                                )}
                              </p>
                            )}
                            {!note.player_name && note.note_type === 'trial_player' && note.trial_player_name && (
                              <p className="text-sm text-slate-500 mb-1 flex items-center gap-2">
                                Trial Player: {note.trial_player_name}
                                <Link 
                                  to={`/admin/trial-requests?search=${encodeURIComponent(note.trial_player_name)}`}
                                  className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 text-xs"
                                  data-testid={`view-trial-${note.id}`}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  View Trial
                                </Link>
                              </p>
                            )}
                            <p className="text-sm text-slate-600 mb-2">{note.content}</p>
                            <p className="text-xs text-slate-400">
                              {(() => {
                                const [year, month, day] = note.date.split('-').map(Number);
                                const date = new Date(year, month - 1, day);
                                return date.toLocaleDateString('en-US', { 
                                  weekday: 'short', month: 'short', day: 'numeric' 
                                });
                              })()} • {new Date(note.created_at).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkNoteRead(note.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Mark Read
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Read Notes Section */}
            <Card className="border border-slate-200 mt-6">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="font-heading text-lg flex items-center gap-2 text-slate-500">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Read Notes ({readNotes.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {readNotes.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-slate-400 text-sm">No read notes</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                    {readNotes.map((note) => (
                      <div key={note.id} className="p-4 bg-slate-50/50">
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-medium text-slate-600">{note.coach_name}</span>
                              <Badge variant="outline" className="text-xs">
                                {note.note_type === 'class' ? 'Class Note' : 
                                 note.note_type === 'trial_player' ? 'Trial Player' : 'General'}
                              </Badge>
                            </div>
                            {note.class_name && (
                              <p className="text-sm text-slate-500 mb-1">Class: {note.class_name}</p>
                            )}
                            {note.player_name && (
                              <p className="text-sm text-slate-500 mb-1 flex items-center gap-2">
                                Player: {note.player_name}
                                {note.player_id && (
                                  <Link 
                                    to={`/admin/players?search=${encodeURIComponent(note.player_name)}`}
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-xs"
                                    data-testid={`view-player-${note.id}`}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View Player
                                  </Link>
                                )}
                                {note.note_type === 'trial_player' && note.trial_id && (
                                  <Link 
                                    to={`/admin/trial-requests?search=${encodeURIComponent(note.player_name || note.trial_player_name || '')}`}
                                    className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 text-xs"
                                    data-testid={`view-trial-${note.id}`}
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                    View Trial
                                  </Link>
                                )}
                              </p>
                            )}
                            {!note.player_name && note.note_type === 'trial_player' && note.trial_player_name && (
                              <p className="text-sm text-slate-500 mb-1 flex items-center gap-2">
                                Trial Player: {note.trial_player_name}
                                <Link 
                                  to={`/admin/trial-requests?search=${encodeURIComponent(note.trial_player_name)}`}
                                  className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 text-xs"
                                  data-testid={`view-trial-${note.id}`}
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  View Trial
                                </Link>
                              </p>
                            )}
                            <p className="text-sm text-slate-500 mb-2">{note.content}</p>
                            <p className="text-xs text-slate-400">
                              {(() => {
                                const [year, month, day] = note.date.split('-').map(Number);
                                const date = new Date(year, month - 1, day);
                                return date.toLocaleDateString('en-US', { 
                                  weekday: 'short', month: 'short', day: 'numeric' 
                                });
                              })()} • Read at: {note.read_at ? new Date(note.read_at).toLocaleString() : 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {selectedCoach?.coach_name} - Time Entries
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {selectedCoach?.entries?.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No entries for this month</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Coach Notes</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Admin Notes/Reason</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedCoach?.entries?.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {(() => {
                            // Parse date without timezone conversion - entry.date is YYYY-MM-DD
                            const [year, month, day] = entry.date.split('-').map(Number);
                            const date = new Date(year, month - 1, day);
                            return date.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            });
                          })()}
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-semibold">{entry.hours_worked} hrs</span>
                            {entry.expected_hours && Math.abs(entry.hours_worked - entry.expected_hours) > 0.1 && (
                              <div className="mt-1">
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Expected: {entry.expected_hours}h
                                </Badge>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{entry.class_name || '-'}</TableCell>
                        <TableCell className="max-w-xs">
                          <div className="space-y-1">
                            {entry.notes && <p className="text-sm truncate">{entry.notes}</p>}
                            {entry.duration_mismatch_reason && (
                              <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                                Mismatch: {entry.duration_mismatch_reason}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(entry.status)}</TableCell>
                        <TableCell className="max-w-xs">
                          <div className="space-y-1">
                            {entry.admin_note && (
                              <p className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                                <span className="font-medium">Edit: </span>{entry.admin_note}
                              </p>
                            )}
                            {entry.review_notes && (
                              <p className={`text-xs px-2 py-1 rounded ${
                                entry.status === 'rejected' ? 'text-rose-600 bg-rose-50' : 'text-slate-600 bg-slate-50'
                              }`}>
                                <span className="font-medium">
                                  {entry.status === 'rejected' ? 'Rejection: ' : 'Review: '}
                                </span>
                                {entry.review_notes}
                              </p>
                            )}
                            {!entry.admin_note && !entry.review_notes && (
                              <span className="text-slate-400 text-xs">-</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {entry.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReviewEntry(entry.id, 'approved')}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`approve-${entry.id}`}
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleReviewEntry(entry.id, 'rejected')}
                                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                  data-testid={`reject-${entry.id}`}
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {entry.status === 'approved' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditEntryDialog(entry)}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                data-testid={`edit-${entry.id}`}
                                title="Edit approved hours"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Rate Dialog */}
        <Dialog open={rateDialog} onOpenChange={setRateDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Set Hourly Rate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{rateCoach?.name}</p>
                <p className="text-sm text-slate-500">{rateCoach?.email}</p>
              </div>
              <div className="space-y-2">
                <Label>Hourly Rate ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newRate}
                  onChange={(e) => setNewRate(e.target.value)}
                  placeholder="e.g., 25.00"
                  data-testid="hourly-rate-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setRateDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={handleSetRate}
                  data-testid="save-rate-btn"
                >
                  Save Rate
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Entry Dialog */}
        <Dialog open={editEntryDialog} onOpenChange={setEditEntryDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Edit2 className="h-5 w-5 text-blue-500" />
                Edit Approved Hours
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Important</p>
                  <p className="text-amber-700">Please communicate any changes to the coach directly. This edit will be logged for audit purposes.</p>
                </div>
              </div>
            </div>

            {editingEntry && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Entry Date</p>
                  <p className="font-medium">
                    {(() => {
                      // Parse date without timezone conversion
                      const [year, month, day] = editingEntry.date.split('-').map(Number);
                      const date = new Date(year, month - 1, day);
                      return date.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      });
                    })()}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">Original Hours</p>
                  <p className="font-medium">{editingEntry.hours_worked} hours</p>
                </div>

                <div className="space-y-2">
                  <Label>New Hours <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={editEntryData.hours_worked}
                    onChange={(e) => setEditEntryData({...editEntryData, hours_worked: e.target.value})}
                    placeholder="e.g., 2.0"
                    data-testid="edit-hours-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Reason for Edit <span className="text-red-500">*</span></Label>
                  <Textarea
                    value={editEntryData.notes}
                    onChange={(e) => setEditEntryData({...editEntryData, notes: e.target.value})}
                    placeholder="Please provide a reason for this edit (required)..."
                    rows={3}
                    data-testid="edit-reason-input"
                  />
                  <p className="text-xs text-slate-500">This note will be visible in the time entry history</p>
                </div>

                <DialogFooter className="mt-4">
                  <Button variant="outline" onClick={() => setEditEntryDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    className="bg-blue-500 hover:bg-blue-600"
                    onClick={handleEditEntry}
                    disabled={editSubmitting || !editEntryData.notes.trim()}
                    data-testid="save-edit-btn"
                  >
                    {editSubmitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default CoachBilling;
